import React from "react";
import { Plus, X } from "lucide-react";
import { useBuilder, BlockConfig, LineItem } from "../BuilderContext";

const fmt = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const DisbursementsTableBlock: React.FC<{ block: BlockConfig }> = ({ block }) => {
  const { state, dispatch } = useBuilder();
  const heading = block.settings.headingOverride || "Disbursements";

  const disbs = state.lineItems.filter((li) => li.kind === "disbursement");
  const disbIndices = state.lineItems.map((li, i) => li.kind === "disbursement" ? i : -1).filter((i) => i >= 0);

  const handleAdd = () => {
    dispatch({
      type: "ADD_LINE_ITEM",
      item: { kind: "disbursement", description: "", quantity: 1, unitPriceCents: 0, amountCents: 0, taxable: true, taxAmountCents: 0 },
    });
  };

  const handleUpdate = (dIdx: number, field: string, value: any) => {
    const realIdx = disbIndices[dIdx];
    const item = state.lineItems[realIdx];
    const updated: Partial<LineItem> = { [field]: value };
    if (field === "quantity" || field === "unitPriceCents") {
      const qty = field === "quantity" ? value : item.quantity;
      const upc = field === "unitPriceCents" ? value : item.unitPriceCents;
      updated.amountCents = Math.round(qty * upc);
    }
    if (field === "amountCents") {
      const qty = item.quantity || 1;
      updated.unitPriceCents = Math.round(value / qty);
    }
    dispatch({ type: "UPDATE_LINE_ITEM", index: realIdx, item: updated });
  };

  const handleRemove = (dIdx: number) => {
    dispatch({ type: "REMOVE_LINE_ITEM", index: disbIndices[dIdx] });
  };

  return (
    <div className="py-4">
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-3">{heading}</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b-2" style={{ borderColor: "var(--color-primary)" }}>
            <th className="text-left py-2 pr-2 text-xs font-semibold text-slate-600">Description</th>
            <th className="text-right py-2 px-2 text-xs font-semibold text-slate-600 w-20">Qty</th>
            <th className="text-right py-2 px-2 text-xs font-semibold text-slate-600 w-28">Unit Price</th>
            <th className="text-right py-2 pl-2 text-xs font-semibold text-slate-600 w-28">Amount</th>
            <th className="w-8 print:hidden" />
          </tr>
        </thead>
        <tbody>
          {disbs.map((li, dIdx) => (
            <tr key={dIdx} className="border-b border-slate-100 transition-colors hover:bg-slate-50/60 group">
              <td className="py-2 pr-2">
                <input
                  type="text"
                  value={li.description}
                  onChange={(e) => handleUpdate(dIdx, "description", e.target.value)}
                  className="w-full bg-white/80 border border-slate-200 text-sm text-slate-800 rounded-lg px-2.5 py-1 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all print:border-0 print:bg-transparent print:ring-0 print:p-0 print:rounded-none"
                  placeholder="Description"
                />
              </td>
              <td className="py-2 px-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={li.quantity}
                  onChange={(e) => handleUpdate(dIdx, "quantity", parseFloat(e.target.value) || 0)}
                  className="w-full bg-white/80 border border-slate-200 text-sm text-right text-slate-800 rounded-lg px-2.5 py-1 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all print:border-0 print:bg-transparent print:ring-0 print:p-0 print:rounded-none"
                />
              </td>
              <td className="py-2 px-2">
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={li.unitPriceCents ? (li.unitPriceCents / 100).toFixed(2) : ""}
                  onChange={(e) => handleUpdate(dIdx, "unitPriceCents", Math.round(parseFloat(e.target.value || "0") * 100))}
                  className="w-full bg-white/80 border border-slate-200 text-sm text-right text-slate-800 rounded-lg px-2.5 py-1 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all print:border-0 print:bg-transparent print:ring-0 print:p-0 print:rounded-none"
                />
              </td>
              <td className="py-2 pl-2">
                <div className="relative">
                  <span className="absolute left-1.5 top-1/2 -translate-y-1/2 text-xs text-slate-400 pointer-events-none select-none">$</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={li.amountCents ? (li.amountCents / 100).toFixed(2) : ""}
                    onChange={(e) => handleUpdate(dIdx, "amountCents", Math.round(parseFloat(e.target.value || "0") * 100))}
                    className="w-full bg-white/80 border border-slate-200 text-sm text-right font-medium text-slate-800 rounded-lg pl-5 pr-2.5 py-1 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:bg-white transition-all print:border-0 print:bg-transparent print:ring-0 print:p-0 print:rounded-none"
                    placeholder="0.00"
                  />
                </div>
              </td>
              <td className="py-2 text-center print:hidden">
                <button onClick={() => handleRemove(dIdx)} className="p-1 rounded hover:bg-red-50 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X className="h-3.5 w-3.5" />
                </button>
              </td>
            </tr>
          ))}
          {disbs.length === 0 && (
            <tr>
              <td colSpan={5} className="py-6 text-center text-sm text-slate-400">No disbursements</td>
            </tr>
          )}
        </tbody>
      </table>
      <div className="mt-3 print:hidden">
        <button
          onClick={handleAdd}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border border-dashed border-slate-300 text-slate-500 hover:border-emerald-400 hover:text-emerald-700 hover:bg-emerald-50/50 transition-all"
        >
          <Plus className="h-3.5 w-3.5" /> Add Disbursement
        </button>
      </div>
    </div>
  );
};

export default DisbursementsTableBlock;
